import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { PlaceholderBlock } from "@/components/aklive/PlaceholderBlock";
import { EASE, MaskedLines } from "@/components/aklive/motion";

const SLIDES = [
  { label: "[BRAND SYSTEM — LANDSCAPE]" },
  { label: "[WEBSITE MOCKUP — 16:9]" },
  { label: "[EVENT BRANDING — WIDE]" },
];

export const Hero = () => {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 5200);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <section ref={ref} className="pt-28 md:pt-40" data-testid="hero-section">
      <div className="container-ak">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
          className="mb-6 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.24em] text-ink2 md:mb-10 md:text-xs"
        >
          <span className="text-ember">01</span>
          <span>Ethos — A visual manifesto</span>
        </motion.p>

        <h1 className="font-display text-[13vw] font-bold leading-[0.95] tracking-[-0.03em] text-ink sm:text-7xl md:text-8xl lg:text-[6rem]">
          <MaskedLines
            delay={0.25}
            lines={[
              "Built for what",
              <span key="l2">
                comes next<span className="text-ember">.</span>
              </span>,
            ]}
          />
        </h1>

        <motion.p
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease: EASE }}
          className="mt-8 max-w-[560px] text-base leading-relaxed text-ink2 md:mt-10 md:text-lg"
        >
          A design-led business partner — combining business thinking with design execution.
        </motion.p>
      </div>

      <div className="container-ak mt-12 md:mt-16">
        <motion.div
          initial={reduce ? { opacity: 0 } : { clipPath: "inset(100% 0% 0% 0%)" }}
          animate={reduce ? { opacity: 1 } : { clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
          className="relative"
        >
          <motion.div style={reduce ? undefined : { y: parallaxY }} className="relative h-[55vh] w-full overflow-hidden md:h-[62vh]">
            <AnimatePresence>
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="absolute inset-0"
              >
                <PlaceholderBlock label={SLIDES[index].label} fill />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>

        <div className="mt-5 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.2em] text-ink2 md:text-xs" data-testid="hero-slide-counter">
            {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-3">
            {SLIDES.map((s, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show visual ${i + 1}`}
                data-testid={`hero-slide-btn-${i}`}
                onClick={() => setIndex(i)}
                className="flex h-11 items-center"
              >
                <span
                  className={`block h-[2px] w-10 transition-colors duration-300 ${i === index ? "bg-ink" : "bg-line"}`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
